/**
 * features/admin/TeachingDaysSettings.tsx
 * ------------------------------------------------------------------
 * School Settings → Teaching Days. LKC's timetable runs a 6-day cycle that
 * rolls on through teaching days and pauses on days without teaching (the
 * school, 31 Oct 2026). Weekends, public holidays (HR → Public Holidays) and
 * the breaks between terms (School Settings → Term Dates) pause it on their
 * own. What is set here:
 *   - the date the cycle counts from, and which cycle day it is;
 *   - any other day without teaching that comes up during the year.
 * And it shows the result: today's cycle day and the coming weeks, day by
 * day, so a wrong setting is seen before a teacher opens the wrong lessons.
 *
 * Stored at reportSchoolSettings/teachingCycle (rules: admin, head, academic
 * admin write). The rule is lib/teachingCycle.ts (tested).
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { doc } from 'firebase/firestore';
import { CalendarCheck2, Plus, Save, Trash2 } from 'lucide-react';
import { db, setDoc } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { AccentButton, AccentPanel, GradientHero, PanelHeader, SoftCard, glassFieldClass } from '@/features/finance/ui';
import {
  TEACHING_CYCLE_DOC, addDays, cycleCalendar, cycleDayOf, gaboroneToday, noTeachingReason, weekdayOf,
  type CycleClosure, type TeachingCycleSettings,
} from '@/lib/teachingCycle';
import { useTeachingCycle } from '@/lib/useTeachingCycle';

const longDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const shortDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

export default function TeachingDaysSettings() {
  const { userData } = useAuth();
  const live = useTeachingCycle();
  const [draft, setDraft] = useState<TeachingCycleSettings>(live.settings);
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => { if (live.ready) setDraft(live.settings); }, [live.ready, live.settings]);

  const today = gaboroneToday();
  const anchorProblem = draft.anchorDate ? noTeachingReason(draft.anchorDate, draft, live.ctx) : null;
  const todayReason = noTeachingReason(today, draft, live.ctx);
  const todayDay = cycleDayOf(today, draft, live.ctx);
  /* From the Monday of this week, six weeks — enough to see a break come and go. */
  const weekStart = addDays(today, -((weekdayOf(today) + 6) % 7));
  const calendar = useMemo(() => cycleCalendar(weekStart, 42, draft, live.ctx), [weekStart, draft, live.ctx]);
  const dirty = JSON.stringify({ ...draft, updatedAt: 0, updatedBy: 0, updatedByName: 0 })
    !== JSON.stringify({ ...live.settings, updatedAt: 0, updatedBy: 0, updatedByName: 0 });

  function addClosure() {
    if (!newDate) return;
    const closures: CycleClosure[] = [...draft.closures.filter((c) => c.date !== newDate), { date: newDate, reason: newReason.trim() || 'No teaching' }]
      .sort((a, b) => a.date.localeCompare(b.date));
    setDraft({ ...draft, closures });
    setNewDate(''); setNewReason('');
  }

  async function save() {
    if (anchorProblem) { setMsg({ ok: false, text: `The cycle must count from a teaching day — ${longDate(draft.anchorDate)} is "${anchorProblem}".` }); return; }
    setBusy(true); setMsg(null);
    try {
      await setDoc(doc(db, COLLECTIONS.schoolSettings, TEACHING_CYCLE_DOC), {
        length: draft.length, anchorDate: draft.anchorDate, anchorDay: draft.anchorDay, closures: draft.closures,
        updatedAt: new Date().toISOString(), updatedBy: userData?.uid ?? '', updatedByName: userData?.displayName ?? '',
      });
      setMsg({ ok: true, text: 'Saved. The timetable follows these days from now on.' });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  const field = glassFieldClass();
  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <GradientHero icon={CalendarCheck2} title="Teaching Days"
        subtitle="The 6-day timetable cycle: where it counts from, and the days it pauses" />

      <AccentPanel tone={todayDay ? 'navy' : 'gold'}>
        <p className="text-sm text-gray-500">Today, {longDate(today)}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">
          {todayDay ? `Day ${todayDay}` : todayReason ? `No teaching — ${todayReason}` : 'Not set up yet'}
        </p>
      </AccentPanel>

      <SoftCard pad="p-0">
        <PanelHeader icon={CalendarCheck2} title="Where the cycle counts from"
          subtitle="Any teaching day whose cycle day you know; every other day counts on from it" />
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Date
            <input type="date" className={field} value={draft.anchorDate} onChange={(e) => setDraft({ ...draft, anchorDate: e.target.value })} />
          </label>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">is cycle day
            <select className={field} value={draft.anchorDay} onChange={(e) => setDraft({ ...draft, anchorDay: Number(e.target.value) })}>
              {Array.from({ length: draft.length }, (_, i) => i + 1).map((d) => <option key={d} value={d}>Day {d}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Days in the cycle
            <input type="number" min={1} max={10} className={field} value={draft.length}
              onChange={(e) => setDraft({ ...draft, length: Math.max(1, Math.min(10, Number(e.target.value) || 6)) })} />
          </label>
        </div>
        {anchorProblem && (
          <p className="px-4 pb-3 text-sm text-rose-600">{longDate(draft.anchorDate)} has no teaching ({anchorProblem}). Pick a teaching day.</p>
        )}
      </SoftCard>

      <SoftCard pad="p-0">
        <PanelHeader icon={CalendarCheck2} title="Other days without teaching"
          subtitle="Weekends, public holidays and the breaks between terms pause the cycle on their own" />
        <div className="flex flex-wrap items-end gap-3 p-4">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Date
            <input type="date" className={glassFieldClass('!w-auto')} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
          </label>
          <label className="block flex-1 min-w-[200px] text-xs font-medium text-gray-600 dark:text-gray-300">Why
            <input className={field} value={newReason} onChange={(e) => setNewReason(e.target.value)} placeholder="Sports day, staff development…" />
          </label>
          <AccentButton icon={Plus} variant="soft" onClick={addClosure} disabled={!newDate}>Add day</AccentButton>
        </div>
        {draft.closures.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-gray-400 italic">None added.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {draft.closures.map((c) => (
              <li key={c.date} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                <span><b>{longDate(c.date)}</b> — {c.reason}</span>
                <button type="button" aria-label={`Remove ${c.date}`} className="text-gray-400 hover:text-rose-600"
                  onClick={() => setDraft({ ...draft, closures: draft.closures.filter((x) => x.date !== c.date) })}>
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </SoftCard>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {msg && <p className={`mr-auto text-sm ${msg.ok ? 'text-emerald-700' : 'text-rose-600'}`}>{msg.text}</p>}
        <AccentButton icon={Save} onClick={() => void save()} disabled={busy || !dirty || !draft.anchorDate}>
          {busy ? 'Saving…' : 'Save teaching days'}
        </AccentButton>
      </div>

      <SoftCard pad="p-0">
        <PanelHeader icon={CalendarCheck2} title="The next six weeks" subtitle={draft.anchorDate ? 'As the timetable will see them' : 'Set the date the cycle counts from to see cycle days'} />
        <div className="grid grid-cols-2 gap-px bg-gray-100 dark:bg-gray-700/60 sm:grid-cols-7">
          {calendar.filter((c) => weekdayOf(c.date) !== 0).map((c) => (
            <div key={c.date} className={`bg-white dark:bg-gray-800 px-3 py-2 ${c.date === today ? 'ring-2 ring-inset ring-primary' : ''} ${weekdayOf(c.date) === 6 ? 'hidden sm:block' : ''}`}>
              <p className="text-[11px] text-gray-400">{shortDate(c.date)}</p>
              {c.day
                ? <p className="text-sm font-bold text-gray-900 dark:text-white">Day {c.day}</p>
                : <p className="text-xs text-gray-400 truncate" title={c.reason ?? ''}>{c.reason === 'Weekend' ? '—' : c.reason ?? '—'}</p>}
            </div>
          ))}
        </div>
      </SoftCard>
    </div>
  );
}
